import { createReducer } from '@reduxjs/toolkit';
import { EmptyINormalized, INormalized, IGrouped, EmptyIGrouped, buildGroup, TId } from '@app/lib/type';
import { feature, jobs, actions } from './config';
import { createDataSuccessToNormalizedReducer } from '@app/lib/reducer';
import {
  ITestItem,
  ITestSection,
  ITestLine,
  TTestSectionSpecifier,
  ITestLineResult,
  ITestLineMask,
  IGradingProposal,
  upsertTestLineResult,
  evaluateTestLineNominativ,
} from '@app/state';
import { TTestLineStatus } from './types';
import { isEmpty } from 'lodash';

interface IState {
  featureInitialized?: boolean;
  testSection: INormalized<ITestSection>;
  testSectionMap: Partial<Record<TTestSectionSpecifier, ITestSection>>;
  testItem: INormalized<ITestItem>;
  testLine: IGrouped<ITestLine>;
  testLineMask: INormalized<ITestLineMask>;
  gradingProposal: INormalized<IGradingProposal>;
  lineResult: Record<TId, ITestLineResult>;
  lineStatus: Record<TId, TTestLineStatus>;
}

export type TState = IState;

const initialState: TState = {
  testSection: EmptyINormalized(),
  testSectionMap: {},
  testItem: EmptyINormalized(),
  testLine: EmptyIGrouped(),
  testLineMask: EmptyINormalized(),
  gradingProposal: EmptyINormalized(),
  lineResult: {},
  lineStatus: {},
};

export const reducer = createReducer<TState>(initialState, (builder) => {
  builder.addCase(feature.initAction, (state) => {
    state.featureInitialized = true;
  });
  builder.addCase(jobs.loadTestSettings.successAction, (state, data) => {
    createDataSuccessToNormalizedReducer('testSection', 'testSection')(state, data);
    createDataSuccessToNormalizedReducer('testItem', 'testItem')(state, data);
    createDataSuccessToNormalizedReducer('testLine', 'testLine')(state, data);
    createDataSuccessToNormalizedReducer('testLineMask', 'testLineMask')(state, data);
    buildGroup<ITestSection, ITestLine>('testSection', state.testSection, state.testLine, (tl) => tl.lineNumber);
    for (const section of Object.values(state.testSection.map)) state.testSectionMap[section.section] = section;
    createDataSuccessToNormalizedReducer('gradingProposal', 'gradingProposal')(state, data);

    if (isEmpty(state.lineResult)) {
      for (const testLineId of state.testLine.ids) {
        const proposal: IGradingProposal | undefined = state.gradingProposal.map[testLineId];
        if (!!proposal && proposal.proposedBy === 'expert') {
          state.lineResult[testLineId] = evaluateTestLineNominativ(
            upsertTestLineResult(testLineId, state.lineResult[testLineId], {
              femaleArticleR: proposal.femaleArticleR,
              femaleArticleW: proposal.femaleArticleW,
              maleArticleR: proposal.maleArticleR,
              maleArticleW: proposal.maleArticleW,
              maleIndefinitArticleR: proposal.maleIndefinitArticleR,
              maleIndefinitArticleW: proposal.maleIndefinitArticleW,
              neuterArticleR: proposal.neuterArticleR,
              neuterArticleW: proposal.neuterArticleW,
              other: proposal.other
            }),
            state.testLineMask.map[testLineId]
          );
          state.lineStatus[testLineId] = 'closed';
        }
      }
    }
  });
  builder.addCase(actions.updateGrading, (state, { payload: { testLineId, update } }) => {
    state.lineResult[testLineId] = evaluateTestLineNominativ(
      upsertTestLineResult(testLineId, state.lineResult[testLineId], {
        femaleArticleR: false,
        femaleArticleW: false,
        maleArticleR: false,
        maleArticleW: false,
        maleIndefinitArticleR: false,
        maleIndefinitArticleW: false,
        neuterArticleR: false,
        neuterArticleW: false,
        other: false,
        ...update,
      }),
      state.testLineMask.map[testLineId]
    );
    state.lineStatus[testLineId] = 'closed';
  });
  builder.addCase(actions.openGrading, (state, { payload: { testLineId } }) => {
    state.lineStatus[testLineId] = 'open';
  });
});
